import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { includes as _includes } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, skip, switchMap } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { pendingPaymentActions } from '@amfam/billing/pending-payment/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { BillingPaymentPaths } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { LoadingSpinnerService } from '@amfam/ui-kit';

@Injectable({
  providedIn: 'root'
})
export class DeepLinkGuard {
  constructor(
    private store: Store,
    private spinner: LoadingSpinnerService,
    private autoPayUtil: AutoPayUtilService,
    private feature: FeatureFlagService
  ) {}

  canActivate(): Observable<boolean> {
    this.spinner.start({ blockActions: true });

    return combineLatest([
      this.store.select(routerQuery.getRouterState),
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading).pipe(skip(1)),
      this.store.select(AutoPaySelectors.getAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingPolicyList)
    ]).pipe(
      filter(
        ([
          ,
          ,
          ,
          billAccountsLoading,
          automaticPaymentsLoading,
          scheduledPaymentsLoading,
          policyRiskLoading
        ]) =>
          billAccountsLoading === false &&
          automaticPaymentsLoading === false &&
          scheduledPaymentsLoading === false &&
          policyRiskLoading === false
      ),
      switchMap(([routerState, billAccounts, getAutomaticPayments]) => {
        const doesBillAccountNumberExistForUser: boolean = billAccounts.some(
          billAccount =>
            billAccount.billAccountNumber === routerState.state.params.billAccountNumber ||
            billAccount.billAccountNumber === routerState.state.params.id
        );

        const doesBillAccountHaveAutopay: boolean = getAutomaticPayments.some(
          automaticPayment =>
            automaticPayment.billAccountNumber === routerState.state.params.billAccountNumber
        );

        if (doesBillAccountNumberExistForUser === false) {
          this.store.dispatch(
            BillAccountActions.billAccountFound(doesBillAccountNumberExistForUser)
          );
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/overview']
            })
          );
          this.spinner.stop();
          return of(false);
        } else if (_includes(routerState.state.url, 'onetimepay')) {
          this.store.dispatch(
            pendingPaymentActions.AddPendingPaymentAction({
              billAccountNumber: routerState.state.params.billAccountNumber
            })
          );
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
            })
          );

          this.spinner.stop();
          return of(true);
        } else if (_includes(routerState.state.url, 'editautopay')) {
          if (doesBillAccountHaveAutopay === true) {
            this.autoPayUtil.editAutoPay(
              routerState.state.params.billAccountNumber,
              `/billing/${routerState.state.params.billAccountNumber}`
            );
            this.spinner.stop();
            return of(true);
          } else {
            this.store.dispatch(
              BillAccountActions.doesBillAccountHaveAutopay(doesBillAccountHaveAutopay)
            );
            this.spinner.stop();
            this.store.dispatch(
              fromRouterActions.Go({
                path: ['/overview']
              })
            );
            return of(false);
          }
        } else if (_includes(routerState.state.url, 'statementhistory')) {
          return of(true);
        } else {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/overview']
            })
          );
          this.spinner.stop();
          return of(false);
        }
      })
    );
  }
}
